"use client";

import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { generateSpotlights } from "@/lib/api/generated";

/**
 * Spotlights are tailored to the search they're requested for, so the search
 * id (null = the generic, search-less spotlight) is part of every key: text
 * generated for one search must never surface in another search or outside
 * a search.
 */
export function spotlightsQueryKey(
  profileIds: string[],
  searchId: string | null = null,
) {
  const sorted = [...profileIds].sort();
  return ["spotlights", searchId, sorted] as const;
}

function spotlightIdKey(profileId: string, searchId: string | null = null) {
  return ["spotlight", searchId, profileId] as const;
}

export interface UseSpotlightsOptions {
  /** Search the spotlights are tailored to; omit for the generic spotlight. */
  searchId?: string | null;
  enabled?: boolean;
}

export function useSpotlights(
  profileIds: string[],
  { searchId, enabled = true }: UseSpotlightsOptions = {},
) {
  const queryClient = useQueryClient();
  const scopeId = searchId || null;
  const uniqueIds = useMemo(
    () => [...new Set(profileIds.filter(Boolean))],
    [profileIds],
  );

  return useQuery<Record<string, string>>({
    queryKey: spotlightsQueryKey(uniqueIds, scopeId),
    queryFn: async () => {
      const result: Record<string, string> = {};
      const missing: string[] = [];
      for (const id of uniqueIds) {
        const cached = queryClient.getQueryData<string>(
          spotlightIdKey(id, scopeId),
        );
        if (typeof cached === "string" && cached.length > 0) {
          result[id] = cached;
        } else {
          missing.push(id);
        }
      }
      if (missing.length > 0) {
        const data = await generateSpotlights(
          scopeId
            ? { profile_ids: missing, search_id: scopeId }
            : { profile_ids: missing },
        );
        for (const [id, text] of Object.entries(data.spotlights ?? {})) {
          if (typeof text === "string" && text.length > 0) {
            result[id] = text;
            queryClient.setQueryData(spotlightIdKey(id, scopeId), text);
          }
        }
      }
      return result;
    },
    enabled: enabled && uniqueIds.length > 0,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}

export function useSpotlight(
  profileId: string | null | undefined,
  searchId?: string | null,
) {
  const ids = useMemo(() => (profileId ? [profileId] : []), [profileId]);
  const query = useSpotlights(ids, { searchId, enabled: !!profileId });
  const spotlight = profileId ? query.data?.[profileId] : undefined;
  return {
    spotlight,
    isLoading: !!profileId && !spotlight && (query.isLoading || query.isFetching),
  };
}
